import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast, Toaster } from "sonner";
import { LogIn } from "lucide-react";

const REDIRECT_BY_ROLE = {
    customer: "/",
    doctor: "/doctor",
    admin: "/admin",
    editor: "/admin",
    distributor: "/distributor",
    employee: "/employee",
};

export default function PortalLogin() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const u = await login(email, password);
            toast.success(`Welcome ${u.name}`);
            nav(next || REDIRECT_BY_ROLE[u.role] || "/");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Invalid email or password");
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-md mx-auto px-4 py-16" data-testid="portal-login">
            <Toaster position="top-center" />
            <div className="text-center mb-8">
                <div className="label-eyebrow text-sky-700">Sign in</div>
                <h1 className="font-serif text-4xl text-stone-900 mt-2">Welcome back</h1>
                <p className="text-sm text-stone-600 mt-2">
                    Use the same email &amp; password whether you're a customer, doctor, distributor, employee, editor, or admin — we'll take you to the right place automatically.
                </p>
            </div>

            <form onSubmit={submit} className="bg-white border border-sky-100 p-6 space-y-4">
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-stone-300 mt-1" required autoFocus data-testid="login-email" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Password</Label>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none border-stone-300 mt-1" required data-testid="login-password" />
                </div>
                <Button type="submit" disabled={busy} className="rounded-none w-full bg-sky-700 hover:bg-sky-800 text-stone-50 py-6" data-testid="login-submit">
                    {busy ? "Signing in…" : (<><LogIn className="w-4 h-4 mr-2" />Sign in</>)}
                </Button>
            </form>

            <div className="text-center text-xs text-stone-500 mt-6 space-x-2">
                <span>New customer?</span>
                <Link to="/register" className="text-sky-700 underline">Create account</Link>
                <span>·</span>
                <Link to="/enroll-doctor" className="text-sky-700 underline">Enroll as doctor</Link>
                <span>·</span>
                <Link to="/distributor/register" className="text-sky-700 underline">Distributor application</Link>
            </div>
            <div className="mt-6 border border-sky-200 bg-sky-50/60 p-4 text-center" data-testid="staff-signup-cta">
                <div className="text-[10px] uppercase tracking-widest text-sky-700 mb-1">HPL team member?</div>
                <p className="text-xs text-stone-700 mb-2">
                    Employees, editors and admins can request an account — pick your designation while signing up and admin will approve it.
                </p>
                <Link
                    to="/staff-register"
                    className="inline-flex items-center justify-center text-sm font-medium text-white bg-sky-700 hover:bg-sky-800 px-4 py-2 transition-colors"
                    data-testid="staff-signup-link"
                >
                    Sign up as staff →
                </Link>
            </div>
        </div>
    );
}
